import React, { useState, useEffect } from 'react';
import CssBaseline from '@mui/material/CssBaseline';

import AuthPage from './pages/AuthPage';
import { useAuthContext } from './context/AuthContext';
import { useCognitoContext } from './context/CognitoContext';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import ErrorFallback from './components/ErrorFallback/ErrorFallback';
import Router from './components/Router';
import UpdateModal from './components/UpdateModal/UpdateModal';
import useIdleTimer from './hooks/useIdleTimer';
import useActivityMonitor from './hooks/useActivityMonitor';
import PageSpinner from './components/PageSpinner';
import { useAppContext } from './context/appContext';
import BasicSnackbar from './components/common/BasicSnackbar/BasicSnackbar';
import { ThemeProvider } from './context/ThemeContext';
import { ScreenResolutionProvider } from './context/screenResolutionContext';
import UpdateWarningModal from './components/UpdateWarningModal/UpdateWarningModal';
import { CompatibilityMatrixProvider } from './context/compatibilityMatrixContext';
import UpdateInstalledModal from './components/UpdateInstalledModal/UpdateInstalledModal';
import useInitializeBackendStatus from './hooks/useInitializeBackendStatus';
import useRebootMonitor from './hooks/useRebootMonitor';
import { useAppConfig } from './context/AppConfigContext.jsx';
import logger from './utils/logger';
import logError from './utils/logError';
import { wakeDisplayOnActivity } from './utils/displayUtils';

export default function App() {
  const { isAuthenticated, setIsAuthenticated, userData, setUserData } = useAuthContext();
  const { getAuthenticatedUser, signOut } = useCognitoContext();
  const [isLoading, setIsLoading] = useState(true); // New loading state
  const { isAcquireRunning } = useAppContext();
  const { api } = useAppConfig();
  const authBaseUrl = api.auth.baseUrl;
  const acquireBaseUrl = api.acquire.baseUrl;

  const isTester = userData && userData.groups && userData.groups.includes('Reyedar-tester');
  const backendAuthenticated = localStorage.getItem('isBackendAuthenticated') === 'true';
  const shouldInitializeBackendStatus = isAuthenticated && backendAuthenticated && !isTester;

  const onIdle = () => {
    // If the user is authenticated and no data acquisition is running, sign out due to inactivity
    if (isAuthenticated && !isAcquireRunning) {
      setIsAuthenticated(false);
      signOut();
    }
  };

  const keepAliveAction = async () => {
    // Ongoing UI activity should keep the display awake too, not just login
    // and test-start — throttled internally so this doesn't spam the acquire
    // server on every tick.
    if (acquireBaseUrl) {
      wakeDisplayOnActivity(acquireBaseUrl);
    }

    // Skip keep-alive action if data acquisition is running
    if (isAcquireRunning) {
      return;
    }
    if (!authBaseUrl) {
      return;
    }
    try {
      const response = await fetch(`${authBaseUrl}/keep-alive`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'X-CSRF-Token': localStorage.getItem('csrfToken'),
        },
      });

      if (!response.ok) {
        const errorResponse = await response.json(); // Assuming error details are also sent as JSON
        throw new Error(`Error: ${errorResponse.message || response.statusText}`);
      }
    } catch (error) {
      logger.error(logError(error, { action: 'keepAliveAction' }));
    }
  };

  // const idleDuration = 60 * 60 * 1000; // 1 hour
  const idleDuration = 12 * 60 * 60 * 1000; // 12 hour
  const keepAliveInterval = 10 * 1000;

  useIdleTimer(idleDuration, onIdle);
  useActivityMonitor(idleDuration, onIdle, keepAliveInterval, keepAliveAction);

  useEffect(() => {
    const checkAuthentication = async () => {
      try {
        const authenticatedUser = await getAuthenticatedUser();
        if (authenticatedUser) {
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        logger.error(logError(error, { action: 'checkAuthentication' }));
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false); // Set loading to false when authentication check is done
      }
    };
    checkAuthentication(); //load user

    return () => {};
  }, [isAuthenticated]);

  useEffect(() => {
    // Load initial data from localStorage
    const email = localStorage.getItem('userEmail') || '';
    const groups = JSON.parse(localStorage.getItem('groups')) || [];
    const firstName = localStorage.getItem('firstName') || '';
    const lastName = localStorage.getItem('lastName') || '';
    setUserData({ email, groups, firstName, lastName });
  }, []);
  useInitializeBackendStatus(shouldInitializeBackendStatus);
  useRebootMonitor();

  return (
    <ThemeProvider>
      <ScreenResolutionProvider>
        <CssBaseline />
        <CompatibilityMatrixProvider>
          <BasicSnackbar />
          <ErrorBoundary fallback={ErrorFallback} resetKeys={[isAuthenticated]}>
            {isLoading ? (
              <PageSpinner />
            ) : !isAuthenticated ? (
              <AuthPage hideSighUp={true} />
            ) : (
              <>
                <UpdateWarningModal />
                <UpdateModal />
                <UpdateInstalledModal />
                <Router />
              </>
            )}
          </ErrorBoundary>
        </CompatibilityMatrixProvider>
      </ScreenResolutionProvider>
    </ThemeProvider>
  );
}
